/**
 * NeoManagerTile — Feb 2026
 * =========================
 *
 * Command Centre admin tile for Neo, HiBFF's third fixed-anchor trainer
 * (the Challenges Companion, sibling to Maya + Rebecca).
 *
 * Two tabs, mirroring RebeccaManagerTile:
 *
 *   • Identity — first name, portrait URL, voice slot, persona
 *     instruction. Optional "Apply to all existing teens" cascade patches
 *     every teen's Neo AvA in-place.
 *
 *   • Scripted Conversations — founder-authored verbatim messages Neo
 *     delivers on trigger events (`first_session`, `second_session`,
 *     `lapse_day_2`, `post_challenge_complete`, `on_demand`). LLM bypass
 *     guarantees the challenge pitch never drifts.
 *
 * Kept intentionally leaner than RebeccaManagerTile (no voice-preview
 * audio, no idle-loop video panel) — those can be lifted in later
 * iterations without touching the wire format. Backend contract is a
 * strict mirror of `/api/rebecca-manager/*`.
 */
import React, { useEffect, useState } from 'react';
import {
  User as UserIcon,
  MessageSquare,
  Plus,
  Trash2,
  Pencil,
  Save,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';
import { apiClient } from '../../api/client';

const TRIGGER_LABELS = {
  first_session: 'First session',
  second_session: 'Second session',
  lapse_day_2: 'Day-2 silent (lapse)',
  post_challenge_complete: 'After a challenge complete',
  on_demand: 'On demand (manual)',
};

// Male-only voice slots — Neo is male. Uses the same modulation map as
// the AvA Library so voice previews are consistent across the app.
const NEO_VOICE_OPTIONS = ['tm1','tm2','tm3','tm4','tm5','tm6','tm7','tm8','tm9','tm10']
  .map((v, i) => ({ value: v, label: `Male Voice ${i + 1}` }));

// ── Identity panel ─────────────────────────────────────────────────────

const IdentityPanel = () => {
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiClient.request('/api/neo-manager/identity');
        setIdentity(data);
      } catch (e) {
        setError(e?.message || 'Failed to load Neo identity');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setError(null); setSuccess(null); setSaving(true);
    try {
      const payload = {
        first_name: identity.first_name,
        portrait_url: identity.portrait_url,
        voice_slot: identity.voice_slot,
        instruction_template: identity.instruction_template,
        apply_to_existing: applyToAll,
      };
      const res = await apiClient.request('/api/neo-manager/identity', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      setIdentity(res.identity);
      setSuccess(
        applyToAll
          ? `Saved. Cascaded to ${res.cascade_count} existing teen${res.cascade_count === 1 ? '' : 's'}.`
          : 'Saved. Future teens will use the new template.'
      );
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8" data-testid="neo-identity-loading"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!identity) return <div className="text-sm text-red-600">{error || 'Unable to load identity'}</div>;

  return (
    <div className="space-y-4" data-testid="neo-identity-panel">
      <div className="flex gap-4 items-start">
        <img
          src={identity.portrait_url}
          alt="Neo"
          className="w-24 h-24 rounded-xl object-cover bg-gray-100 border border-gray-200"
          data-testid="neo-identity-portrait-preview"
          onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
        />
        <div className="flex-1 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">First name</label>
            <input
              type="text"
              value={identity.first_name || ''}
              onChange={(e) => setIdentity({ ...identity, first_name: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              data-testid="neo-identity-first-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Portrait URL (external URLs are scanned by celebrity gate on save)</label>
            <input
              type="text"
              value={identity.portrait_url || ''}
              onChange={(e) => setIdentity({ ...identity, portrait_url: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              data-testid="neo-identity-portrait-url"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Voice slot</label>
            <select
              value={identity.voice_slot || 'tm2'}
              onChange={(e) => setIdentity({ ...identity, voice_slot: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              data-testid="neo-identity-voice-slot"
            >
              {NEO_VOICE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label} ({o.value})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Persona / instruction (tone, scope, guardrails)
        </label>
        <textarea
          value={identity.instruction_template || ''}
          onChange={(e) => setIdentity({ ...identity, instruction_template: e.target.value })}
          rows={10}
          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md font-mono focus:outline-none focus:ring-2 focus:ring-purple-500/40"
          data-testid="neo-identity-instruction"
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={applyToAll}
          onChange={(e) => setApplyToAll(e.target.checked)}
          className="rounded"
          data-testid="neo-identity-apply-to-all"
        />
        Apply to all existing teens (cascades to every Neo AvA)
      </label>

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="neo-identity-error">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2" data-testid="neo-identity-success">
          {success}
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
        data-testid="neo-identity-save"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Save
      </button>
    </div>
  );
};

// ── Scripts panel ──────────────────────────────────────────────────────

const EMPTY_SCRIPT = {
  script_id: null,
  title: '',
  trigger_event: 'first_session',
  message: '',
  order_index: 0,
  active: true,
};

const ScriptsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // draft script or null
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/neo-manager/scripts');
      setRows(data.rows || []);
    } catch (e) {
      setError(e?.message || 'Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true); setError(null);
    try {
      if (editing.script_id) {
        const { script_id, ...body } = editing;
        await apiClient.request(`/api/neo-manager/scripts/${script_id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        });
      } else {
        const body = { ...editing };
        delete body.script_id;
        await apiClient.request('/api/neo-manager/scripts', {
          method: 'POST', body: JSON.stringify(body),
        });
      }
      setEditing(null);
      await reload();
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scriptId) => {
    if (!window.confirm('Delete this script?')) return;
    try {
      await apiClient.request(`/api/neo-manager/scripts/${scriptId}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  if (loading) return <div className="flex justify-center py-8" data-testid="neo-scripts-loading"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-3" data-testid="neo-scripts-panel">
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div className="text-xs text-gray-500">
          {rows.length} script{rows.length === 1 ? '' : 's'} · delivered verbatim on trigger, LLM bypass.
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...EMPTY_SCRIPT })}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-purple-600 text-white text-xs font-semibold hover:bg-purple-700"
          data-testid="neo-scripts-add"
        >
          <Plus size={14} /> Add script
        </button>
      </div>

      <div className="border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs font-semibold text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Trigger</th>
              <th className="px-3 py-2 text-left">Order</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid="neo-scripts-table">
            {rows.map((r) => (
              <tr key={r.script_id} data-testid={`neo-script-row-${r.script_id}`}>
                <td className="px-3 py-2 text-gray-800">{r.title}</td>
                <td className="px-3 py-2 text-gray-600 text-xs">{TRIGGER_LABELS[r.trigger_event] || r.trigger_event}</td>
                <td className="px-3 py-2 text-gray-600 text-xs">{r.order_index}</td>
                <td className="px-3 py-2 text-xs">
                  {r.active
                    ? <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded">On</span>
                    : <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Off</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      type="button"
                      onClick={() => setEditing({ ...r })}
                      className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                      data-testid={`neo-script-edit-${r.script_id}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(r.script_id)}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                      data-testid={`neo-script-delete-${r.script_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400 text-sm">No scripts yet &mdash; add one to override Neo&apos;s default opener.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="neo-script-editor">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">
                {editing.script_id ? 'Edit script' : 'New script'}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1 text-gray-400 hover:text-gray-700"
                data-testid="neo-script-editor-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Title (admin-only label)</label>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                  data-testid="neo-script-title"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Trigger event</label>
                  <select
                    value={editing.trigger_event}
                    onChange={(e) => setEditing({ ...editing, trigger_event: e.target.value })}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md"
                    data-testid="neo-script-trigger"
                  >
                    {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Order</label>
                  <input
                    type="number"
                    value={editing.order_index}
                    onChange={(e) => setEditing({ ...editing, order_index: parseInt(e.target.value || '0', 10) })}
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md"
                    data-testid="neo-script-order"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Message (delivered verbatim — {'{teen_name}'} is auto-replaced)</label>
                <textarea
                  value={editing.message}
                  onChange={(e) => setEditing({ ...editing, message: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                  data-testid="neo-script-message"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  data-testid="neo-script-active"
                />
                Active (delivered on trigger)
              </label>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !editing.title || !editing.message}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-50"
                data-testid="neo-script-save"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tile shell (two tabs) ──────────────────────────────────────────────

const NeoManagerTile = () => {
  const [tab, setTab] = useState('identity');
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="neo-manager-tile">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 bg-gradient-to-r from-indigo-50 to-white">
        <UserIcon size={18} className="text-indigo-600" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-800">Neo Manager</div>
          <div className="text-xs text-gray-500">Challenges companion — identity + scripted openers.</div>
        </div>
      </div>
      <div className="px-4 pt-3 border-b border-gray-100 flex gap-2">
        <button
          type="button"
          onClick={() => setTab('identity')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-t-md ${tab === 'identity' ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="neo-tab-identity"
        >
          <UserIcon size={12} className="inline mr-1" /> Identity
        </button>
        <button
          type="button"
          onClick={() => setTab('scripts')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-t-md ${tab === 'scripts' ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
          data-testid="neo-tab-scripts"
        >
          <MessageSquare size={12} className="inline mr-1" /> Scripted Conversations
        </button>
      </div>
      <div className="p-4">
        {tab === 'identity' ? <IdentityPanel /> : <ScriptsPanel />}
      </div>
    </div>
  );
};

export default NeoManagerTile;
